import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Grid, TextField, Button, useTheme, Typography, Alert, Box, Link, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { handleRegister, reset } from 'app/modules/account/register/register.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { signupSchema } from 'app/utils/validation-schema/signup-schema';
import LoaderIcon from 'app/components/common/loader-icon/loader-icon';
import axios from 'axios';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber, type Country } from 'react-phone-number-input';
import { detectCountryFromTimezone } from 'app/utils/common-utils/common-utils';
// @ts-ignore — CSS import from react-phone-number-input has no type declaration
import 'react-phone-number-input/style.css';

interface RegistrationPageProps {
  currentComponent?: any;
  setCurrentComponent?: any;
  handleAuthModalClose?: any;
  isAdmin?: boolean;
}

const RegistrationPage = ({ currentComponent, setCurrentComponent, handleAuthModalClose, isAdmin }: RegistrationPageProps) => {
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [isdisabled, setIsDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [ageTier, setAgeTier] = useState<'BLOCKED' | 'CONSENT_REQUIRED' | 'NORMAL'>('NORMAL');
  const isMinor = ageTier === 'CONSENT_REQUIRED';
  const isBlocked = ageTier === 'BLOCKED';

  // Phone OTP state
  const [defaultCountry] = useState<Country>(() => (detectCountryFromTimezone() as Country) ?? 'IN');
  const [phoneValue, setPhoneValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');

  const handleClickShowPassword = () => setShowPassword(prev => !prev);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(prev => !prev);

  const initialValues = {
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    parentEmail: '',
    phoneNumber: '',
    countryCode: '',
    isoCode: '',
  };

  const getAgeTier = (dob: string, countryCode: string): 'BLOCKED' | 'CONSENT_REQUIRED' | 'NORMAL' => {
    if (!dob || countryCode !== '+91') return 'NORMAL';
    const birthDate = new Date(dob);
    const today = new Date();
    const age =
      today.getFullYear() -
      birthDate.getFullYear() -
      (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0);
    if (age < 16) return 'BLOCKED';
    if (age < 18) return 'CONSENT_REQUIRED';
    return 'NORMAL';
  };

  const handleSubmit = values => {
    setIsDisabled(true);
    const { firstName, lastName, email, password, dateOfBirth, parentEmail, phoneNumber, countryCode, isoCode } = values;
    const payload: any = { firstName, lastName, email, password, langKey: 'en' };
    if (dateOfBirth) payload.dateOfBirth = dateOfBirth;
    if (isMinor && parentEmail) payload.parentEmail = parentEmail;
    if (phoneVerified && phoneNumber) {
      payload.phoneNumber = phoneNumber;
      payload.countryCode = countryCode;
      payload.isoCode = isoCode;
    }
    dispatch(handleRegister(payload));
  };

  const handleSendOtp = async () => {
    if (!phoneValue) return;
    setPhoneError('');
    try {
      const phoneInfo = parsePhoneNumber(phoneValue);
      if (!phoneInfo || !isValidPhoneNumber(phoneValue)) {
        setPhoneError('Please enter a valid phone number.');
        return;
      }
      const countryCode = `+${phoneInfo.countryCallingCode}`;
      const nationalNumber = phoneInfo.nationalNumber;
      setOtpSending(true);
      await axios.post('api/phone/sendOtp', { phoneNumber: nationalNumber, countryCode, medium: 'sms' });
      setOtpSent(true);
    } catch (err) {
      setPhoneError('Failed to send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) return;
    setOtpError('');
    const phoneInfo = parsePhoneNumber(phoneValue);
    if (!phoneInfo) {
      setOtpError('Invalid phone number.');
      return;
    }
    const countryCode = `+${phoneInfo.countryCallingCode}`;
    const nationalNumber = phoneInfo.nationalNumber;
    const isoCode = phoneInfo.country || '';
    try {
      setOtpVerifying(true);
      const res = await axios.post('api/phone/verify-pre-registration', {
        phoneNumber: nationalNumber,
        countryCode,
        isoCode,
        code: otpCode,
      });
      if (res.data?.verified) {
        setPhoneVerified(true);
        formik.setFieldValue('phoneNumber', nationalNumber);
        formik.setFieldValue('countryCode', countryCode);
        formik.setFieldValue('isoCode', isoCode);
        const tier = getAgeTier(formik.values.dateOfBirth, countryCode);
        setAgeTier(tier);
        if (tier !== 'CONSENT_REQUIRED') formik.setFieldValue('parentEmail', '');
      } else {
        setOtpError('Incorrect OTP. Please try again.');
      }
    } catch {
      setOtpError('Incorrect OTP. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const navigate = useNavigate();
  const theme = useTheme();
  const loading = useAppSelector(state => state.register.loading);
  const response = useAppSelector(state => state.register.successMessage);
  const errorMessage = useAppSelector(state => state.register.errorMessage);

  const heading = {
    color: theme.textColor.heading,
    marginBottom: '13px',
    marginTop: '2px',
    fontSize: '15px',
    width: '98%',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: signupSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (response !== null) {
      if (typeof response === 'string') {
        setMessage(response);
      } else if (typeof response === 'object') {
        if (response?.error?.response?.data?.message) {
          setMessage(response?.error?.response?.data?.message);
        }
      }
    }
  }, [response]);

  const handleGoBack = () => setCurrentComponent('login');

  useEffect(() => {
    const emailInput = document.getElementById('reg-email') as HTMLInputElement | null;
    const passwordInput = document.getElementById('reg-password') as HTMLInputElement | null;
    if (emailInput) emailInput.value = '';
    if (passwordInput) passwordInput.value = '';
    return () => { dispatch(reset()); };
  }, []);

  const fetchEmail = async () => {
    try {
      const res = await axios.get<any>(`/api/invitee-email?token=${localStorage.getItem('token')}`);
      if (res) setInviteEmail(res?.data?.data);
    } catch (error) {
      console.log('Error fetching goal:', error);
    }
  };

  useEffect(() => {
    if (inviteEmail !== '') formik.setFieldValue('email', inviteEmail);
  }, [inviteEmail]);

  useEffect(() => {
    if (localStorage.getItem('token')) fetchEmail();
  }, [localStorage.getItem('token'), localStorage?.getItem('invitation')]);

  const isSubmitDisabled = !formik.dirty || !formik.isValid || loading || isdisabled || !phoneVerified || isBlocked;

  return (
    <Grid container spacing={2} direction={'column'} justifyContent={'center'} alignItems={'center'} px={8} py={3} sx={{ margin: 'auto' }}>
      <Box
        sx={{
          border: 'none',
          maxHeight: '80vh',
          maxWidth: '460px',
          textAlign: 'center',
          overflowY: 'scroll',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { display: 'none' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'secondary.contrastText', height: '10px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}
      >
        <div onClick={() => navigate('/')} className="d-flex justify-content-center">
          <img src="content/images/logos/reachamy-logo.webp" alt="reach amy logo" style={{ height: '58px' }} />
        </div>
        <h2 className="d-flex justify-content-center" style={heading}>Sign up</h2>

        <form id="registration-form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            {/* First name */}
            <Grid item xs={6} md={6} className="pt-1">
              <TextField
                size="small" fullWidth name="firstName" placeholder="First name"
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName ? formik.errors.firstName.toString() : ''}
                value={formik.values.firstName}
                onChange={e => { if (/^[A-Za-z]*$/.test(e.target.value)) formik.handleChange(e); }}
                onBlur={formik.handleBlur} autoComplete="off"
              />
            </Grid>
            {/* Last name */}
            <Grid item xs={6} md={6} className="pt-1">
              <TextField
                size="small" fullWidth name="lastName" placeholder="Last name"
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName.toString() : ''}
                value={formik.values.lastName}
                onChange={e => { if (/^[A-Za-z]*$/.test(e.target.value)) formik.handleChange(e); }}
                onBlur={formik.handleBlur} autoComplete="off"
              />
            </Grid>
            {/* Email */}
            <Grid item xs={12} md={12} className="pt-3">
              <TextField
                disabled={isAdmin} size="small" fullWidth name="email" id="reg-email"
                placeholder="Email" type="email"
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email ? formik.errors.email.toString() : ''}
                value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} autoComplete="off"
              />
            </Grid>
            {/* Phone number with OTP */}
            <Grid item xs={12} md={12} className="pt-3">
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Phone number
                </Typography>
                <style>{`input.PhoneInputInput { border-radius: 8px !important; border: 1px solid #bfbfbf !important; padding: 8px; font-size: 14px; }`}</style>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <PhoneInput
                      placeholder="Enter phone number"
                      value={phoneValue}
                      defaultCountry={defaultCountry}
                      onChange={val => {
                        setPhoneValue(val || '');
                        if (phoneVerified) {
                          setPhoneVerified(false);
                          formik.setFieldValue('phoneNumber', '');
                          formik.setFieldValue('countryCode', '');
                          formik.setFieldValue('isoCode', '');
                          setAgeTier('NORMAL');
                        }
                        setOtpSent(false);
                        setPhoneError('');
                        setOtpCode('');
                        setOtpError('');
                      }}
                      limitMaxLength
                      countryCallingCodeEditable={false}
                      international
                    />
                  </Box>
                  {phoneVerified ? (
                    <>
                      <Typography variant="caption" color="success.main" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>✓ Verified</Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => { setPhoneVerified(false); setOtpSent(false); setOtpCode(''); setOtpError(''); formik.setFieldValue('phoneNumber', ''); formik.setFieldValue('countryCode', ''); formik.setFieldValue('isoCode', ''); setAgeTier('NORMAL'); }}
                        sx={{ textTransform: 'none', fontSize: '11px', color: theme.palette.primary.light, minWidth: 'unset', p: 0 }}
                      >
                        Change
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSendOtp}
                      disabled={!phoneValue || otpSending}
                      sx={{ whiteSpace: 'nowrap', borderRadius: '8px', textTransform: 'none', fontSize: '12px',
                        backgroundColor: 'primary.main', boxShadow: 'none',
                        '&:hover': { backgroundColor: 'primary.main', boxShadow: 'none' } }}
                    >
                      {otpSending ? <LoaderIcon /> : otpSent ? 'Resend' : 'Send OTP'}
                    </Button>
                  )}
                </Box>
                {phoneError && <Typography variant="caption" color="error">{phoneError}</Typography>}
              </Box>
            </Grid>
            {/* OTP input — appears after Send OTP */}
            {otpSent && !phoneVerified && (
              <Grid item xs={12} md={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder="Enter 6-digit OTP"
                    value={otpCode}
                    onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                    inputProps={{ maxLength: 6, style: { letterSpacing: '4px', fontWeight: 600 } }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleVerifyOtp}
                    disabled={otpCode.length < 4 || otpVerifying}
                    sx={{ whiteSpace: 'nowrap', borderRadius: '8px', textTransform: 'none', fontSize: '12px',
                      backgroundColor: theme.palette.primary.light, color: 'white', boxShadow: 'none',
                      '&:hover': { backgroundColor: theme.palette.primary.light, boxShadow: 'none' } }}
                  >
                    {otpVerifying ? <LoaderIcon /> : 'Verify'}
                  </Button>
                </Box>
                {otpError && <Typography variant="caption" color="error">{otpError}</Typography>}
                {!otpError && (
                  <Typography variant="caption" color="text.secondary">OTP sent via SMS. Enter it above to verify.</Typography>
                )}
              </Grid>
            )}
            {/* DOB */}
            <Grid item xs={12} md={12} className="pt-3">
              <TextField
                size="small" fullWidth name="dateOfBirth" placeholder="Date of Birth"
                type="date" InputLabelProps={{ shrink: true }} label="Date of Birth"
                error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth ? formik.errors.dateOfBirth.toString() : ''}
                value={formik.values.dateOfBirth}
                onChange={e => {
                  formik.handleChange(e);
                  const tier = getAgeTier(e.target.value, formik.values.countryCode);
                  setAgeTier(tier);
                  if (tier !== 'CONSENT_REQUIRED') formik.setFieldValue('parentEmail', '');
                }}
                onBlur={formik.handleBlur}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            {/* Blocked — under 16 from India */}
            {isBlocked && (
              <Grid item xs={12} md={12}>
                <Alert severity="error" sx={{ maxWidth: '100%' }}>
                  <Typography variant="subtitle1" fontSize={'10px'}>
                    You must be at least 16 years old to create an account.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {/* Parent email — only if Indian minor */}
            {isMinor && (
              <Grid item xs={12} md={12} className="pt-3">
                <TextField
                  size="small" fullWidth name="parentEmail"
                  placeholder="Parent / Guardian Email" type="email" label="Parent / Guardian Email"
                  error={formik.touched.parentEmail && Boolean(formik.errors.parentEmail)}
                  helperText={
                    formik.touched.parentEmail && formik.errors.parentEmail
                      ? formik.errors.parentEmail.toString()
                      : 'Required for Indian users aged 16 and 17 (DPDP Act)'
                  }
                  value={formik.values.parentEmail}
                  onChange={formik.handleChange} onBlur={formik.handleBlur} autoComplete="off"
                />
              </Grid>
            )}
            {/* Password */}
            <Grid item xs={12} md={12} className="pt-3">
              <TextField
                size="small" fullWidth name="password" id="reg-password" placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password ? formik.errors.password.toString() : ''}
                value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {/* Confirm password */}
            <Grid item xs={12} md={12} className="pt-3">
              <TextField
                size="small" fullWidth name="confirmPassword" placeholder="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword ? formik.errors.confirmPassword.toString() : ''}
                value={formik.values.confirmPassword} onChange={formik.handleChange} onBlur={formik.handleBlur}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                      {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {/* Alerts */}
            <Grid item xs={12} md={12}>
              {message?.length > 0 && (
                <Alert sx={{ maxWidth: '100%' }}>
                  <Typography variant="subtitle1" fontSize={'10px'}>{message}</Typography>
                </Alert>
              )}
              {errorMessage?.length > 0 && (
                <Alert severity="error" sx={{ maxWidth: '100%' }}>
                  <Typography variant="subtitle1" fontSize={'10px'} color={theme.palette.error.main}>{errorMessage}</Typography>
                </Alert>
              )}
            </Grid>
            {/* T&C */}
            <Grid item xs={12} md={12} pt={0} className="pt-0" sx={{ textAlign: 'justify' }}>
              <span style={{ fontSize: '11px', textAlign: 'center', width: '70%', fontWeight: '600' }}>
                Your information is safe and secure. By signing up, you agree to our{' '}
                <span style={{ marginLeft: '1px', marginRight: '1px', color: theme.palette.primary.light, textTransform: 'capitalize', fontSize: '11px' }}>
                  <span style={{ cursor: 'pointer' }} onClick={() => navigate('/privacy-policy')}>privacy policy</span>, &nbsp;
                  <span style={{ cursor: 'pointer' }} onClick={() => navigate('/cookie-policy')}>cookie policy</span>, &nbsp;
                  <span style={{ cursor: 'pointer' }} onClick={() => navigate('/member-agreement')}>Member agreement</span>
                </span>
                &nbsp;and that we may share your personal information with our partners to verify your account.
              </span>
            </Grid>
            {/* Submit */}
            <Grid item xs={12} md={12} className="text-center pt-2">
              <Button
                type="submit" variant="contained" color="primary"
                sx={{
                  marginBottom: '10px', height: theme.createActivity.height,
                  backgroundColor: theme.palette.primary.light, borderRadius: theme.createActivity.borderRadius,
                  color: theme.palette.secondary.dark, borderColor: theme.palette.primary.main,
                  boxShadow: 'none',
                  '&:active, &:focus, &:hover': { backgroundColor: theme.createActivity.backgroundColor.hover, boxShadow: 'none' },
                }}
                endIcon={loading ? <LoaderIcon /> : null}
                disabled={isSubmitDisabled}
              >
                Create Account
              </Button>
              {!phoneVerified && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Verify your phone number to continue
                </Typography>
              )}
            </Grid>
            {!isAdmin && (
              <Grid item xs={12} md={12} pt={0} className="pt-0">
                <span style={{ fontSize: '11px', textAlign: 'center', width: '70%', fontWeight: '600' }}>Back to </span>
                <Link onClick={handleGoBack} sx={{ fontSize: '11px', color: theme.palette.primary.light, textAlign: 'center', width: '70%', fontWeight: '600' }}>
                  Log in
                </Link>
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </Grid>
  );
};

export default RegistrationPage;
